// src/components/AdminView.tsx

import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchMembers, fetchFaults, deleteUser, createUser, fetchFAQ, deleteFAQ, updateMemberPermissions } from "../api";
import { type User as MemberDto, type Fault, FAQ, MemberPermissionDTO } from "../types";

import cardStyles from "../styles/Card.module.css";
import tableStyles from "../styles/Table.module.css";
import buttonStyles from "../styles/Button.module.css";
import adminStyles from "../styles/AdminView.module.css";
import { useAuth } from "react-oidc-context";

import { GlobalContext } from "../App";
import { AlertModalContext } from "./AlertModal";
import AdminEquipment from "./AdminEquipment";

export default function AdminView() {
  const auth = useAuth();
  const { site } = useParams<{ site: string }>();
  const { globalSite, setSite } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState<MemberDto[]>([]);
  const [faults, setFaults] = useState<Fault[]>([]);
  const [faq, setFaq] = useState<FAQ[]>([]);
  const [role, setRole] = useState("staff");

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [newUserGuid, setNewUserGuid] = useState("");
  const [newUserSentence, setNewUserSentence] = useState("");
  const [newUserName, setNewUserName] = useState("");

  // alert modal state
  const { setShowAlertModal, setAlertMessage } = useContext(AlertModalContext);

  // modal open/close
  const openAlertModal = (text:string) => {
    setAlertMessage(text);
    setShowAlertModal(true);
  }

  // load users
  const loadUsers = () => {
    if (!site) return;
    fetchMembers(site)
      .then(setUsers)
      .catch((err) => console.error("Error fetching users:", err));
  };
  useEffect(() => {
    if (site) {
      setSite(site);
      loadUsers();
    }
  }, [site, setSite]);

  // load faults
  useEffect(() => {
    fetchFaults().then(setFaults).catch(console.error);
  }, []);

  useEffect(() => {
    if (site !== undefined) {
      fetchFAQ(site).then(setFaq).catch(console.error);
    }
  }, [])

  // get role
  useEffect(() => {
    setRole(String(auth.user?.profile.role || "staff"));
  }, [auth.user?.profile.role]);

  // delete user
  const handleDeleteUser = async (id: number) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await deleteUser(id);
      loadUsers();
    } catch (err) {
      console.error(err);
      openAlertModal("Could not delete user.");
    }
  };

  // modal open/close
  const openModal = () => setModalOpen(true);
  const closeModal = () => {
    setModalOpen(false);
    setNewUserGuid("");
    setNewUserSentence("");
    setNewUserName("");
  };

  // create user
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserSentence.trim() || !newUserName.trim()) {
      openAlertModal("All fields are required.");
      return;
    }
    try {
      await createUser(site!, {
        sentence: newUserSentence.trim(),
        name: newUserName.trim(),
      });
      closeModal();
      loadUsers();
    } catch (err: any) {
      openAlertModal(err.message || "Failed to create user.");
    }
  };

  // delete faq
  function handleDeleteFaq(faqToDelete:FAQ) {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      deleteFAQ(globalSite, faqToDelete.id);
      setFaq(faq.filter(f => f.id != faqToDelete.id))
    } catch (err) {
      console.error(err);
      openAlertModal("Could not delete faq");
    }
  };

  const handleCheckboxChange = (e:ChangeEvent<HTMLInputElement>) => {
    let user = users.find(u => u.id === Number(e.target.id))
    if (user === undefined) {
      return;
    }

    let dto:MemberPermissionDTO = {
      id: user.id,
      sentence: user.sentence,
      canCreateTickets: e.target.name === "create" ? e.target.checked : user.canCreateTickets,
      canRespond: e.target.name === "respond" ? e.target.checked : user.canRespond
    }

    updateMemberPermissions(user.id, dto).then((r) => {
      const updatedUsers = users.map(u => {
        if (u.id !== r.id) {
          return u;
        } else {
          return r;
        }
      })
      setUsers(updatedUsers);
    })
  }

  const renderFaultsSection = () =>
    role === "super" && (
      <section className={adminStyles.section}>
        <div className={adminStyles.header}>
          <h3>Faults</h3>
          <button
            className={`${buttonStyles.button} ${buttonStyles.primary}`}
            onClick={() => navigate(`/admin/${site}/faults/new`)}
          >
            Add Fault
          </button>
        </div>
        <table className={tableStyles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Description</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {faults.map((f) => (
              <tr key={f.id}>
                <td>#{f.id}</td>
                <td>{f.description}</td>
                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  <button
                    className={`${buttonStyles.buttonSmall} ${buttonStyles.primary}`}
                    onClick={() =>
                      navigate(`/admin/${site}/faults/${f.id}/edit`)
                    }
                    style={{ marginRight: 8 }}
                  >
                    Edit
                  </button>
                  <button
                    className={`${buttonStyles.buttonSmall} ${buttonStyles.close}`}
                    // onClick={() => handleDeleteFault(f.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {faults.length === 0 && (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", padding: 16 }}>
                  No faults found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    );

    const renderFAQSection = () => 
      role === "super" && (
        <section className={adminStyles.section}>
          <div className={adminStyles.header}>
            <h3>FAQ</h3>
            <button
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              onClick={() =>  navigate(`/admin/${site}/faq/new`)}
            >
              Add FAQ
            </button>
          </div>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Question</th>
                <th>Answer</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {faq.map((f) => (
                <tr key={f.id}>
                  <td>{f.question}</td>
                  <td>{f.answer}</td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  <button
                    className={`${buttonStyles.buttonSmall} ${buttonStyles.primary}`}
                    onClick={() =>
                      navigate(`/admin/${site}/FAQ/${f.id}/edit`)
                    }
                    style={{ marginRight: 8 }}
                  >
                    Edit
                  </button>
                  <button
                    className={`${buttonStyles.buttonSmall} ${buttonStyles.close}`}
                    onClick={() => handleDeleteFaq(f)}
                  >
                    Remove
                  </button>
                </td>
                </tr>
              ))}
              {faq.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: "center", padding: 16 }}>
                    No FAQs Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      );

  return (
    <>
      <div className={cardStyles.card}>
        <h2>Administration</h2>
        
        {/* Users */}
        <section className={adminStyles.section}>
          <div className={adminStyles.header}>
            <h3>Users</h3>
            <button
              className={`${buttonStyles.button} ${buttonStyles.primary}`}
              onClick={openModal}
            >
              Add User
            </button>
          </div>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Sentence</th>
                <th>Name</th>
                <th style={{ textAlign: "center" }}>Can Create Tickets</th>
                <th style={{ textAlign: "center" }}>Can Respond to Tickets</th>
                <th style={{ textAlign: "center" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.sentence}</td>
                  <td>{u.name}</td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      id={`${u.id}`}
                      name="create"
                      checked={u.canCreateTickets}
                      onChange={handleCheckboxChange}
                      type="checkbox"
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      id={`${u.id}`}
                      name="respond"
                      checked={u.canRespond}
                      onChange={handleCheckboxChange}
                      type="checkbox"
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className={`${buttonStyles.buttonSmall} ${buttonStyles.close}`}
                      onClick={() => handleDeleteUser(u.id)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: "center", padding: 16 }}>
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {renderFaultsSection()}
        {renderFAQSection()}
        <AdminEquipment />
      </div>

      {/* Add User Modal */}
      {modalOpen && (
        <div className={adminStyles.modalOverlay} onClick={closeModal}>
          <div
            className={`${cardStyles.card} ${adminStyles.modalContent}`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Add New User</h3>
            <form className={adminStyles.modalForm} onSubmit={handleCreateUser}>

              <div className={adminStyles.modalField}>
                <label htmlFor="sentence">Sentence</label>
                <input
                  id="sentence"
                  value={newUserSentence}
                  onChange={(e) => setNewUserSentence(e.target.value)}
                  required
                />
              </div>

              <div className={adminStyles.modalField}>
                <label htmlFor="name">Name</label>
                <input
                  id="name"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  required
                />
              </div>

              <div className={adminStyles.modalActions}>
                <button
                  type="button"
                  className={`${buttonStyles.button} ${buttonStyles.secondary}`}
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`${buttonStyles.button} ${buttonStyles.primary}`}
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
